
import React, { useState } from 'react';
import { CalendarEvent } from '@/types/calendar';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { format } from 'date-fns';
import { MapPin, Clock, Calendar as CalendarIcon, Edit, Trash, Check } from 'lucide-react';

interface EventItemProps {
  event: CalendarEvent;
  onUpdate: (event: CalendarEvent) => void;
  onDelete: (eventId: string) => void;
  showDate?: boolean;
}

const EventItem: React.FC<EventItemProps> = ({ event, onUpdate, onDelete, showDate = false }) => {
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editedEvent, setEditedEvent] = useState<CalendarEvent>(event);
  
  const handleUpdateEvent = () => {
    onUpdate(editedEvent);
    setShowEditDialog(false);
  };
  
  const getSourceColor = () => {
    switch (event.source) {
      case 'google':
        return 'bg-blue-500';
      case 'apple':
        return 'bg-gray-500';
      default:
        return event.color ? event.color : 'bg-purple-500';
    }
  };
  
  const getSourceBadge = () => {
    switch (event.source) {
      case 'google':
        return <Badge variant="outline" className="bg-blue-500/10 text-blue-500 border-blue-200">Google</Badge>;
      case 'apple':
        return <Badge variant="outline" className="bg-gray-500/10 text-gray-500 border-gray-200">Apple</Badge>;
      default:
        return <Badge variant="outline" className="bg-purple-500/10 text-purple-500 border-purple-200">Local</Badge>;
    }
  };
  
  return (
    <>
      <Card className="overflow-hidden hover:shadow-md transition-shadow">
        <div className={`h-1 ${getSourceColor()}`} />
        <CardContent className="p-3">
          <div className="flex justify-between items-start">
            <div className="space-y-1 flex-1">
              <div className="flex items-start justify-between">
                <h3 className="font-medium line-clamp-1">{event.title}</h3>
                {getSourceBadge()}
              </div>
              
              {showDate && (
                <p className="text-xs text-muted-foreground flex items-center">
                  <CalendarIcon className="h-3 w-3 mr-1" />
                  {format(new Date(event.start), 'EEE, MMM d')}
                </p>
              )}
              
              <p className="text-xs text-muted-foreground flex items-center">
                <Clock className="h-3 w-3 mr-1" />
                {format(new Date(event.start), 'h:mm a')} - {format(new Date(event.end), 'h:mm a')}
              </p>
              
              {event.location && (
                <p className="text-xs text-muted-foreground flex items-center">
                  <MapPin className="h-3 w-3 mr-1" />
                  {event.location}
                </p>
              )}
              
              {event.description && (
                <p className="text-xs text-muted-foreground line-clamp-2 mt-1">{event.description}</p>
              )}
            </div>
            
            <div className="flex items-center gap-1 ml-2">
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-7 w-7" 
                onClick={() => setShowEditDialog(true)}
              >
                <Edit className="h-3 w-3" />
              </Button>
              
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-7 w-7 text-destructive hover:text-destructive" 
                onClick={() => onDelete(event.id)}
              >
                <Trash className="h-3 w-3" />
              </Button>
              
              {event.isCompleted !== undefined && (
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className={`h-7 w-7 ${event.isCompleted ? 'text-green-500' : 'text-muted-foreground'}`} 
                  onClick={() => onUpdate({ ...event, isCompleted: !event.isCompleted })}
                >
                  <Check className="h-3 w-3" />
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
      
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Edit Event</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="title">Event Title</Label>
              <Input
                id="title"
                value={editedEvent.title}
                onChange={(e) => setEditedEvent({ ...editedEvent, title: e.target.value })}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={editedEvent.description || ''}
                onChange={(e) => setEditedEvent({ ...editedEvent, description: e.target.value })}
                placeholder="Add a description..."
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="location">Location</Label>
              <Input
                id="location"
                value={editedEvent.location || ''}
                onChange={(e) => setEditedEvent({ ...editedEvent, location: e.target.value })}
                placeholder="Add a location..."
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={handleUpdateEvent}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default EventItem;
